<template>
    <div class="cz2__step-options-header">
        <div class="cz2__step-options-header-title-wrapper">
            <div class="cz2__step-options-header-name-wrapper">
                <main-navigation class="cz2__main-navigation" />

                <p
                    v-if="boxName"
                    class="cz2__step-options-header-pop-name"
                >
                    {{ boxName }}
                </p>
            </div>

            <h2
                v-if="subMenuTitle && showSubMenuTitle"
                class="cz2__step-options-header-sub-menu-title"
                :class="headerFontClass"
            >
                <span v-if="isSpellingBee">{{ $t('EM_DASH') }}</span>
                {{ subMenuTitle }}
                <span v-if="isSpellingBee">{{ $t('EM_DASH') }}</span>
            </h2>

            <price-disclaimer v-if="hasPrice" />

            <h3
                v-if="subMenuSubTitle && showSubMenuTitle"
                class="cz2__step-options-header-sub-menu-subtitle"
                :class="[
                    isSpellingBee && 'font-proxima',
                ]"
            >
                {{ subMenuSubTitle }}
            </h3>
        </div>

        <color-selector
            v-if="showColorSwatches && !isSubMenuOpen"
            :colors="colors"
        />

        <!-- Added for body step skin tones. No time to fight with the color selector. -->
        <slot />
    </div>
</template>

<style lang="scss">
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .cz2__step-options-header {
            // Prevent selection cards from overlapping the drop shadow
            z-index: 2;
            position: relative;

            box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.16);
            padding-bottom: 10px;

            width: 100%;

            display: flex;
            flex-direction: column;
            gap: 8px;

            font-family: 'Inter Bold';
            color: color(primaryWhite);

            .cz2__step-options-header-name-wrapper {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 16px;

                .cz2__main-navigation {
                    flex: none;
                }
            }

            .cz2__step-options-header-pop-name {
                text-transform: uppercase;
                font-size: 18px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .cz2__step-options-header-title-wrapper {
                padding: 0 var(--layout-spacing);

                display: flex;
                flex-direction: column;
                gap: 12px;
                flex: none;
            }

            .cz2__step-options-header-sub-menu-title,
            .cz2__step-options-header-sub-menu-subtitle {
                font-style: italic;
            }

            .cz2__step-options-header-sub-menu-title {
                font-size: 32px;
                text-transform: uppercase;
                line-height: 1;
            }

            .cz2__step-options-header-sub-menu-subtitle {
                margin-left: -8px;
                font-size: 24px;
            }
        }

        .cz2__steps-options__info {
            position: relative;

            width: 42px;
            height: 42px;

            margin-left: 0px;

            background-size: 16px 16px;
            background-repeat: no-repeat;
            background-position: center center;
        }

        @include respond-above(lg) {
            .cz2__step-options-header {
                padding-top: 52px;

                .cz2__step-options-header-name-wrapper {
                    gap: 32px;
                }

                .cz2__step-options-header-pop-name {
                    font-size: 24px;
                }

                .cz2__step-options-header-sub-menu-title {
                    margin-left: -12px;
                    font-size: 46px;
                }

                .cz2__steps-options__info {
                    background-position: left top;
                }
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        // Kiosk does not need extra padding since we are not switching to the side by side layout.
        .cz2__step-options-header {
            padding-top: 52px;
            padding-bottom: 8px;

            @include respond-above(2xl) {
                display: block;
            }
        }

        .cz2__step-options-header-sub-menu-title {
            font-size: 81px;
            margin-left: 18px;
        }

        .cz2__step-options-header-sub-menu-subtitle {
            margin-left: 16px;
        }

        .cz2__step-options-header-pop-name {
            font-size: 32px;
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //   ▗▄▄▖▗▄▄▖ ▗▄▄▄▖▗▖   ▗▖   ▗▄▄▄▖▗▖  ▗▖ ▗▄▄▖    ▗▄▄▖ ▗▄▄▄▖▗▄▄▄▖
    //  ▐▌   ▐▌ ▐▌▐▌   ▐▌   ▐▌     █  ▐▛▚▖▐▌▐▌       ▐▌ ▐▌▐▌   ▐▌
    //   ▝▀▚▖▐▛▀▘ ▐▛▀▀▘▐▌   ▐▌     █  ▐▌ ▝▜▌▐▌▝▜▌    ▐▛▀▚▖▐▛▀▀▘▐▛▀▀▘
    //  ▗▄▄▞▘▐▌   ▐▙▄▄▖▐▙▄▄▖▐▙▄▄▖▗▄█▄▖▐▌  ▐▌▝▚▄▞▘    ▐▙▄▞▘▐▙▄▄▖▐▙▄▄
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2.cz2__is-sb {
        .cz2__step-options-header-title-wrapper {
            gap: 8px;

            @include respond-above(lg) {
                width: 100%;
            }
        }

        .cz2__main-navigation-button-alert {
            top: -3px;
            right: -64px;

            @include respond-above(sm) {
                top: -3px;
                right: -64px;
            }

            @include respond-above(md) {
                top: 12px;
                right: -64px;
            }

            @include respond-above(lg) {
                top: 2px;
                right: -15px;
            }
        }

        .cz2__step-options-header-sub-menu-title {
            color: color(sbDarkBlue);
            font-style: normal !important;
            font-size: 16px;
            margin-left: 0 !important;
            font-family: 'Proxima Nova', sans-serif !important;
            text-align: center;
            font-weight: 600;

            @include respond-above(sm) {
                font-size: 35px;
            }
        }

        .cz2__step-options-header-sub-menu-subtitle {
            color: color(sbDarkBlue);
            font-style: normal;
            text-transform: uppercase;

            font-size: 16px;
            padding-left: 10px;

            @include respond-above(sm) {
                font-size: 24px;
            }
        }

        .cz2__step-options-header {
            background-color: transparent;
            flex-wrap: wrap;
            justify-content: center;
            padding-bottom: 0;

            .cz2__main-navigation {
                margin-left: 0;
                padding-bottom: 6px;
            }
        }

        .cz2__main-navigation {
            text-align: center;
        }

        .cz2__color-selector {
            width: 100%;
            display: flex;
            padding-bottom: 0;
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // SPELLING BEE: KIOSK
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2__is-sb.cz2__is-kiosk.cz2__is-sb {
        .cz2__step-options-header {
            @include respond-above(sm) {
                padding-top: 0px;
            }
        }
        .cz2__step-options-header-sub-menu-title {
            font-size: 55px;

            margin-left: 18px;
        }
    }
</style>

<script>
    import * as constants from '../../constants';

    import MainNavigation from '../MainNavigation.vue';
    import ColorSelector from './ColorSelector.vue';
    import PriceDisclaimer from '../PriceDisclaimer.vue';

    const groupMapping = {
        Body: constants.PLACEMENT_BODY,
    };

    export default {
        components: {
            MainNavigation,
            ColorSelector,
            PriceDisclaimer,
        },

        props: {
            hasSubMenu: {
                type: Boolean,
                default: false,
            },
            subMenuTitle: {
                type: String,
                default: '',
            },
            subMenuSubTitle: {
                type: String,
                default: '',
            },
            showSubMenuTitle: {
                type: Boolean,
                default: false,
            },
            showColorSwatches: {
                type: Boolean,
                default: false,
            },
            colors: {
                type: Array,
                default: () => [],
            },
            hasPrice: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            isSpellingBee() {
                return this.$store.state.isSpellingBee;
            },

            currentGroup() {
                return this.$store.state.customizer.currentGroup;
            },

            isHairStep() {
                return constants.HAIR_STEPS.includes(this.currentGroup.code);
            },

            showAlert() {
                return this.$store.state.customizer.alertGroups.includes(this.currentGroup.code);
            },

            viewController() {
                return this.$store.state.customizer.viewController;
            },

            selectedComponent() {
                if (this.currentGroup == null) {
                    return null;
                }

                let placementCode;
                if (this.currentGroup.placements.length === 1) {
                    placementCode = this.currentGroup.placements[0].code;
                } else {
                    placementCode = groupMapping[this.currentGroup.code] || constants.PLACEMENT_SKIN_COLOR;
                }

                const component = this.viewController.selectedAtPlacement(placementCode);

                return component;
            },

            isSubMenuOpen() {
                return this.$store.state.customizer.isSubMenuOpen;
            },

            headerFontClass() {
                return this.$store.getters.headerFontClass;
            },

            boxName() {
                return this.$store.state.customizer.boxName;
            },
        },

        methods: {
            select(option) {
                this.$emit('select', option);
            },
        },
    };
</script>
